<template>
  <header
    class="nav"
    :class="{ 'nav--scrolled': isScrolled }">
    <div class="nav__inner site-inner">
      <!-- Logo -->
      <NuxtLink
        :to="localePath('/')"
        class="nav__logo"
        aria-label="PrePeers - Accueil">
        <img
          src="/logo_prepeers.svg"
          alt=""
          class="nav__logo-img" />
        <span class="nav__logo-text">PrePeers</span>
      </NuxtLink>

      <!-- Navigation desktop -->
      <nav
        ref="navLinksEl"
        class="nav__links"
        role="navigation">
        <div
          v-for="item in navItems"
          :key="item.key"
          class="nav__item"
          @mouseenter="openMenu(item.key)"
          @mouseleave="closeMenu">
          <button
            class="nav__link"
            :class="{ 'nav__link--active': activeMenu === item.key }"
            :aria-expanded="activeMenu === item.key">
            {{ item.label }}
            <svg
              class="nav__chevron"
              :class="{ 'nav__chevron--open': activeMenu === item.key }"
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none">
              <path
                d="M2 4l4 4 4-4"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
          </button>
        </div>
      </nav>

      <!-- Actions desktop -->
      <div class="nav__actions">
        <!-- Non connecté -->
        <template v-if="!userStore.isLoggedIn">
          <NuxtLink
            :to="localePath('/login')"
            class="btn btn-ghost btn-sm"
            >{{ t('nav.login') }}</NuxtLink
          >
          <NuxtLink
            :to="localePath('/register')"
            :prefetch="false"
            class="btn btn-primary btn-sm"
            >{{ t('nav.cta') }}</NuxtLink
          >
        </template>

        <PrePeersLangSwitcher
          display-layout="desktopNavbar"
          @locale-changed="onAppLocaleChanged" />
        <PrePeersChatColorThemeToggle v-if="hasColorThemeToggle" />

        <!-- Connecté -->
        <template v-if="userStore.isLoggedIn">
          <button
            ref="avatarBtnEl"
            class="nav__avatar-btn"
            aria-label="Mon compte"
            :aria-expanded="userMenuOpen"
            @click="toggleUserMenu">
            <div class="nav__avatar-ring">
              <img
                v-if="userStore.avatar"
                :src="userStore.avatar"
                :alt="userStore.firstName ?? ''" />
              <span
                v-else
                class="nav__avatar-initials"
                >{{ userInitials }}</span
              >
            </div>
            <svg
              class="nav__chevron"
              :class="{ 'nav__chevron--open': userMenuOpen }"
              width="12"
              height="12"
              viewBox="0 0 12 12"
              fill="none">
              <path
                d="M2 4l4 4 4-4"
                stroke="currentColor"
                stroke-width="1.5"
                stroke-linecap="round"
                stroke-linejoin="round" />
            </svg>
          </button>
          <Transition name="fade">
            <div
              v-if="userMenuOpen"
              ref="userDropdownEl"
              class="user-dropdown">
              <div class="user-dropdown__header">
                <div class="user-dropdown__identity">
                  <div class="nav__avatar-ring nav__avatar-ring--lg">
                    <img
                      v-if="userStore.avatar"
                      :src="userStore.avatar"
                      :alt="userStore.firstName ?? ''" />
                    <span
                      v-else
                      class="nav__avatar-initials"
                      >{{ userInitials }}</span
                    >
                  </div>
                  <div>
                    <p class="user-dropdown__name">{{ userStore.firstName }}</p>
                    <span
                      v-if="userStore.mbti"
                      class="user-dropdown__mbti"
                      >{{ userStore.mbti }}</span
                    >
                  </div>
                </div>
                <div class="user-dropdown__progress">
                  <div
                    class="progress-bar"
                    style="flex: 1">
                    <div
                      class="progress-bar__fill"
                      :style="{ width: userStore.profileCompletion + '%' }"></div>
                  </div>
                  <span class="user-dropdown__pct">{{ userStore.profileCompletion }}%</span>
                </div>
                <p class="user-dropdown__hint">
                  {{ t('nav.profileCompleteHint') }}
                </p>
                <NuxtLink
                  :to="localePath('/discuter')"
                  class="btn btn-primary"
                  style="width: 100%; justify-content: center"
                  @click="closeUserMenu">
                  {{ t('nav.cta') }}
                </NuxtLink>
              </div>
              <div class="divider-glow"></div>
              <NuxtLink
                :to="localePath('/mon-compte')"
                class="user-dropdown__item"
                @click="closeUserMenu">
                {{ t('nav.accountSettings') }}
              </NuxtLink>
              <div
                class="divider-glow"
                style="opacity: 0.15"></div>
              <button
                class="user-dropdown__item user-dropdown__item--danger"
                @click="handleLogout">
                {{ t('nav.logout') }}
              </button>
            </div>
          </Transition>
        </template>
      </div>

      <!-- Burger mobile -->
      <button
        class="nav__burger"
        :class="{ 'nav__burger--open': mobileOpen }"
        aria-label="Menu"
        @click="toggleMobile">
        <span></span><span></span><span></span>
      </button>
    </div>

    <!-- ── MEGA MENU DESKTOP ── -->
    <Transition name="mega">
      <div
        v-if="activeMenu"
        class="mega-menu"
        @mouseenter="openMenu(activeMenu)"
        @mouseleave="closeMenu">
        <div
          ref="megaMenuPanelEl"
          class="mega-menu__panel">
          <div
            class="mega-menu__inner container"
            @click="onMegaMenuNavClick">
            <!-- Écoles -->
            <template v-if="activeMenu === 'ecoles'">
              <div class="mega-col">
                <NuxtLink
                  :to="localePath('/ecoles')"
                  class="mega-link mega-link--all"
                  >{{ t('nav.ecoles-all') }} →</NuxtLink
                >
                <NuxtLink
                  :to="establishmentSearchPath"
                  class="mega-link mega-link--all mega-link--all-next"
                  >{{ t('nav.search-establishments') }} →</NuxtLink
                >
                <p class="mega-col__title">{{ t('nav.ecoles-all-subtitle') }}</p>
                <ul class="mega-col__list">
                  <li
                    v-for="school in megaMenu.featuredSchools"
                    :key="school.link">
                    <NuxtLink
                      :to="localePath(`/ecoles/${school.link}`)"
                      class="mega-link"
                      >{{ school.title }}</NuxtLink
                    >
                  </li>
                </ul>
              </div>
              <div class="mega-col mega-col--cta">
                <div class="mega-cta">
                  <p class="mega-cta__text">{{ t('nav.ecoles-all-caption-1') }}</p>
                  <NuxtLink
                    :to="localePath(isLoggedIn ? '/discuter' : '/register')"
                    class="btn btn-primary btn-sm"
                    >Discuter avec Lola</NuxtLink
                  >
                  <p class="mega-cta__question">{{ t('nav.ecoles-all-caption-2') }}</p>
                </div>
              </div>
            </template>

            <!-- Types d'écoles -->
            <template v-if="activeMenu === 'types-ecoles'">
              <div class="mega-col mega-col--wide">
                <NuxtLink
                  :to="localePath('/types-ecoles')"
                  class="mega-link mega-link--all"
                  >{{ t('nav.types-all') }} →</NuxtLink
                >
                <NuxtLink
                  :to="establishmentSearchPath"
                  class="mega-link mega-link--all mega-link--all-next"
                  >{{ t('nav.search-establishments') }} →</NuxtLink
                >
                <p class="mega-col__title">{{ t('nav.types-all-subtitle') }}</p>
                <ul class="mega-col__list mega-col__list--grid">
                  <li
                    v-for="type in megaMenu.schoolTypes"
                    :key="type">
                    <NuxtLink
                      :to="localePath(`/types-ecoles/${slugify(type)}`)"
                      class="mega-link"
                      >{{ type }}</NuxtLink
                    >
                  </li>
                </ul>
              </div>
            </template>

            <!-- Où étudier -->
            <template v-if="activeMenu === 'ou-etudier'">
              <div class="mega-col">
                <NuxtLink
                  :to="localePath('/ou-etudier')"
                  class="mega-link mega-link--all"
                  >{{ t('nav.villes-all') }} →</NuxtLink
                >
                <NuxtLink
                  :to="establishmentSearchPath"
                  class="mega-link mega-link--all mega-link--all-next"
                  >{{ t('nav.search-establishments') }} →</NuxtLink
                >
                <p class="mega-col__title">{{ t('nav.villes-all-subtitle') }}</p>
                <ul class="mega-col__list mega-col__list--inline">
                  <li
                    v-for="item in megaMenu.popularCities"
                    :key="item.city">
                    <NuxtLink
                      :to="localePath(`/ou-etudier/${slugify(item.country)}/${slugify(item.city)}`)"
                      class="mega-link"
                      >{{ item.city }}</NuxtLink
                    >
                  </li>
                </ul>
                <p class="mega-col__subtitle">{{ t('nav.villes-all-subtitle-2') }}</p>
                <ul class="mega-col__list mega-col__list--inline">
                  <li
                    v-for="item in megaMenu.trendCities"
                    :key="item.city">
                    <NuxtLink
                      :to="localePath(`/ou-etudier/${slugify(item.country)}/${slugify(item.city)}`)"
                      class="mega-link"
                      >{{ item.city }}</NuxtLink
                    >
                  </li>
                </ul>
              </div>
            </template>

            <!-- Métiers -->
            <template v-if="activeMenu === 'metiers'">
              <div class="mega-col mega-col--wide">
                <NuxtLink
                  :to="localePath('/metiers')"
                  class="mega-link mega-link--all"
                  >{{ t('nav.metiers-all') }} →</NuxtLink
                >
                <p class="mega-col__title">{{ t('nav.metiers-all-subtitle') }}</p>
                <ul class="mega-col__list mega-col__list--grid">
                  <li
                    v-for="sector in megaMenu.jobSectors"
                    :key="sector.link">
                    <NuxtLink
                      :to="localePath(`/metiers/${sector.link}`)"
                      class="mega-link"
                      >{{ sector.title }}</NuxtLink
                    >
                  </li>
                </ul>
              </div>
              <div class="mega-col mega-col--cta">
                <div class="mega-cta">
                  <p class="mega-cta__text">{{ t('nav.metiers-all-caption-1') }}</p>
                  <NuxtLink
                    :to="localePath(isLoggedIn ? '/discuter' : '/register')"
                    class="btn btn-primary btn-sm"
                    >Discuter avec Lola</NuxtLink
                  >
                  <p class="mega-cta__question">{{ t('nav.metiers-all-caption-2') }}</p>
                </div>
              </div>
            </template>

            <!-- Diplômes -->
            <template v-if="activeMenu === 'diplomes'">
              <div class="mega-col mega-col--wide">
                <NuxtLink
                  :to="localePath('/diplomes')"
                  class="mega-link mega-link--all"
                  >{{ t('nav.diplomes-all') }} →</NuxtLink
                >
                <p class="mega-col__title">{{ t('nav.diplomes-all-subtitle') }}</p>
                <ul class="mega-col__list mega-col__list--diplomes">
                  <li
                    v-for="d in megaMenu.diplomas"
                    :key="d.label">
                    <NuxtLink
                      :to="localePath(`/diplomes/${slugify(d.label)}`)"
                      class="mega-link mega-link--diploma">
                      <span class="mega-link__name">{{ d.label }}</span>
                      <span
                        v-if="d.level"
                        class="mega-link__level badge badge-blue"
                        >{{ d.level }}</span
                      >
                    </NuxtLink>
                  </li>
                </ul>
              </div>
            </template>

            <!-- Nos conseils -->
            <template v-if="activeMenu === 'nos-conseils'">
              <div class="mega-col mega-col--wide">
                <NuxtLink
                  :to="localePath('/nos-conseils')"
                  class="mega-link mega-link--all"
                  >{{ t('nav.conseils-all') }} →</NuxtLink
                >
                <p class="mega-col__title">{{ t('nav.conseils-all-subtitle') }}</p>
                <ul class="mega-col__list">
                  <li
                    v-for="article in megaMenu.popularArticles"
                    :key="article.link">
                    <NuxtLink
                      :to="localePath(`/nos-conseils/${article.link}`)"
                      class="mega-link mega-link--article">
                      <span class="mega-link__article-title">{{ article.title }}</span>
                      <span class="mega-link__read-time">{{ article.readTime }}</span>
                    </NuxtLink>
                  </li>
                </ul>
              </div>
            </template>
          </div>
        </div>
        <div
          class="mega-menu__backdrop"
          aria-hidden="true"
          @click="closeMegaMenuImmediate" />
      </div>
    </Transition>
  </header>

  <Teleport to="body">
    <!-- ── MENU MOBILE ── -->
    <Transition name="mobile-menu">
      <div
        v-if="mobileOpen"
        class="mobile-menu">
        <div class="mobile-menu__body">
          <!-- Bloc profil connecté (mobile) -->
          <div
            v-if="userStore.isLoggedIn"
            class="mobile-profile-block">
            <div class="mobile-profile-block__header">
              <div class="mobile-profile-block__identity">
                <div class="nav__avatar-ring nav__avatar-ring--lg">
                  <img
                    v-if="userStore.avatar"
                    :src="userStore.avatar"
                    :alt="userStore.firstName ?? ''" />
                  <span
                    v-else
                    class="nav__avatar-initials"
                    >{{ userInitials }}</span
                  >
                </div>
                <div class="mobile-profile-block__info">
                  <p class="mobile-profile-block__name">{{ userStore.firstName }}</p>
                  <span
                    v-if="userStore.mbti"
                    class="mobile-profile-block__mbti"
                    >{{ userStore.mbti }}</span
                  >
                </div>
              </div>
              <PrePeersChatColorThemeToggle
                v-if="hasColorThemeToggle"
                @click="mobileOpen = false" />
            </div>
            <div class="mobile-profile-block__progress">
              <div
                class="progress-bar"
                style="flex: 1">
                <div
                  class="progress-bar__fill"
                  :style="{ width: userStore.profileCompletion + '%' }"></div>
              </div>
              <span class="mobile-profile-block__pct">{{ userStore.profileCompletion }}%</span>
            </div>
            <p class="mobile-profile-block__hint">
              {{ t('nav.profileCompleteHint') }}
            </p>
          </div>

          <!-- Accordéon nav mobile -->
          <div
            v-for="item in navItems"
            :key="item.key"
            class="mobile-accordion">
            <button
              class="mobile-accordion__trigger"
              :aria-expanded="openAccordion === item.key"
              @click="toggleAccordion(item.key)">
              {{ item.label }}
              <svg
                class="nav__chevron"
                :class="{ 'nav__chevron--open': openAccordion === item.key }"
                width="12"
                height="12"
                viewBox="0 0 12 12"
                fill="none">
                <path
                  d="M2 4l4 4 4-4"
                  stroke="currentColor"
                  stroke-width="1.5"
                  stroke-linecap="round"
                  stroke-linejoin="round" />
              </svg>
            </button>
            <Transition name="accordion">
              <ul
                v-if="openAccordion === item.key"
                class="mobile-accordion__list">
                <li
                  v-for="link in item.links"
                  :key="link.to">
                  <NuxtLink
                    :to="link.to"
                    class="mobile-accordion__link"
                    @click="mobileOpen = false">
                    {{ link.label }}
                  </NuxtLink>
                </li>
              </ul>
            </Transition>
          </div>

          <!-- Liens compte connecté -->
          <template v-if="userStore.isLoggedIn">
            <div
              class="divider-glow"
              style="margin-block: var(--space-2)"></div>
            <NuxtLink
              :to="localePath('/mon-compte')"
              class="mobile-account-link"
              @click="mobileOpen = false">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round">
                <circle
                  cx="12"
                  cy="8"
                  r="4" />
                <path d="M4 20c0-4 3.6-7 8-7s8 3 8 7" />
              </svg>
              {{ t('nav.accountSettings') }}
            </NuxtLink>
            <button
              class="mobile-account-link mobile-account-link--danger"
              @click="handleMobileLogout">
              <svg
                width="16"
                height="16"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                stroke-width="1.8"
                stroke-linecap="round"
                stroke-linejoin="round">
                <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4" />
                <polyline points="16 17 21 12 16 7" />
                <line
                  x1="21"
                  y1="12"
                  x2="9"
                  y2="12" />
              </svg>
              {{ t('nav.logout') }}
            </button>
          </template>
        </div>

        <!-- Footer mobile (fixe) -->
        <div class="mobile-menu__footer">
          <PrePeersLangSwitcher
            display-layout="mobileMenuFooter"
            @locale-changed="onAppLocaleChanged" />
          <PrePeersChatColorThemeToggle
            v-if="!userStore.isLoggedIn && hasColorThemeToggle"
            @click="mobileOpen = false" />
          <template v-if="!userStore.isLoggedIn">
            <div class="mobile-menu__stats">
              <span>10 questions</span>
              <span class="mobile-menu__dot">·</span>
              <span>3 minutes</span>
              <span class="mobile-menu__dot">·</span>
              <span>+50 000 profils révélés</span>
            </div>
            <NuxtLink
              :to="localePath('/login')"
              class="btn btn-ghost btn-sm"
              style="width: 100%; text-align: center"
              @click="mobileOpen = false">
              {{ t('nav.login') }}
            </NuxtLink>
            <NuxtLink
              :to="localePath('/register')"
              :prefetch="false"
              class="btn btn-primary mobile-menu__footer-btn"
              @click="mobileOpen = false">
              {{ t('nav.cta') }}
            </NuxtLink>
          </template>
          <template v-else>
            <NuxtLink
              :to="localePath('/discuter')"
              :prefetch="false"
              class="btn btn-primary mobile-menu__footer-btn"
              @click="mobileOpen = false">
              {{ t('nav.cta') }}
            </NuxtLink>
          </template>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup lang="ts">
import type { ComputedRef, Ref } from 'vue'
import { slugify } from '~/utils/slugify'
import PrePeersLangSwitcher from '~/components/core/PrePeersLangSwitcher.vue'
import PrePeersChatColorThemeToggle from '~/components/chat/PrePeersChatColorThemeToggle.vue'

const { t } = useI18n()
const route = useRoute()
const userStore = useUserStore()
const isLoggedIn = computed(() => userStore.isLoggedIn)

const hasColorThemeToggle: ComputedRef<boolean> = computed(
  (): boolean => route.meta.layout === 'chat' || route.path.includes('/public-profile'),
)

async function logout(): Promise<void> {
  await useNuxtApp().$logout()
}
const localePath: ReturnType<typeof useLocalePath> = useLocalePath()
const establishmentSearchPath: ComputedRef<string> = computed(() =>
  localePath('/recherche/etablissement'),
)
const { megaMenu } = useMegaMenuNavData()

// ── Nav items : source unique desktop + mobile ──────────────
const navItems = computed(() => [
  {
    key: 'ecoles',
    label: t('nav.ecoles'),
    links: [
      { label: t('nav.ecoles-all'), to: localePath('/ecoles') },
      { label: t('nav.search-establishments'), to: establishmentSearchPath.value },
      ...megaMenu.value.featuredSchools.map((s) => ({
        label: s.title,
        to: localePath(`/ecoles/${s.link}`),
      })),
    ],
  },
  {
    key: 'types-ecoles',
    label: t('nav.types'),
    links: [
      { label: t('nav.types-all'), to: localePath('/types-ecoles') },
      { label: t('nav.search-establishments'), to: establishmentSearchPath.value },
      ...megaMenu.value.schoolTypes.map((s) => ({
        label: s,
        to: localePath(`/types-ecoles/${slugify(s)}`),
      })),
    ],
  },
  {
    key: 'ou-etudier',
    label: t('nav.villes'),
    links: [
      { label: t('nav.villes-all'), to: localePath('/ou-etudier') },
      { label: t('nav.search-establishments'), to: establishmentSearchPath.value },
      ...megaMenu.value.popularCities.map((c) => ({
        label: c.city,
        to: localePath(`/ou-etudier/${slugify(c.country)}/${slugify(c.city)}`),
      })),
      ...megaMenu.value.trendCities.map((c) => ({
        label: c.city,
        to: localePath(`/ou-etudier/${slugify(c.country)}/${slugify(c.city)}`),
      })),
    ],
  },
  {
    key: 'metiers',
    label: t('nav.metiers'),
    links: [
      { label: t('nav.metiers-all'), to: localePath('/metiers') },
      ...megaMenu.value.jobSectors.map((s) => ({
        label: s.title,
        to: localePath(`/metiers/${s.link}`),
      })),
    ],
  },
  {
    key: 'diplomes',
    label: t('nav.diplomes'),
    links: [
      { label: t('nav.diplomes-all'), to: localePath('/diplomes') },
      ...megaMenu.value.diplomas.map((d) => ({
        label: d.level ? `${d.label} (${d.level})` : d.label,
        to: localePath(`/diplomes/${slugify(d.label)}`),
      })),
    ],
  },
  {
    key: 'nos-conseils',
    label: t('nav.conseils'),
    links: [
      { label: t('nav.conseils-all'), to: localePath('/nos-conseils') },
      ...megaMenu.value.popularArticles.map((a) => ({
        label: a.title,
        to: localePath(`/nos-conseils/${a.link}`),
      })),
    ],
  },
])

// ── État ───────────────────────────────────────────────────────────────────
const isScrolled = ref(false)
const activeMenu = ref<string | null>(null)
const mobileOpen = ref(false)
const openAccordion = ref<string | null>(null)
const userMenuOpen: Ref<boolean> = ref(false)
const navLinksEl: Ref<HTMLElement | null> = ref(null)
const megaMenuPanelEl: Ref<HTMLElement | null> = ref(null)
const userDropdownEl: Ref<HTMLElement | null> = ref(null)
const avatarBtnEl: Ref<HTMLButtonElement | null> = ref(null)
let closeTimer: ReturnType<typeof setTimeout> | null = null

/**
 * Ferme les menus ouverts après un changement de langue.
 * @returns {void}
 */
function onAppLocaleChanged(): void {
  mobileOpen.value = false
  userMenuOpen.value = false
}

// ── Computed ───────────────────────────────────────────────────────────────
const userInitials = computed(() => {
  if (!userStore.firstName) return '?'
  return userStore.firstName.charAt(0).toUpperCase()
})

// ── Mega menu ──────────────────────────────────────────────────────────────
/**
 * Ouvre le mega menu desktop pour la clé de navigation donnée.
 * @param {string} key - Identifiant de l'entrée de menu (`ecoles`, `metiers`, etc.).
 * @returns {void}
 */
function openMenu(key: string): void {
  if (closeTimer) {
    clearTimeout(closeTimer)
    closeTimer = null
  }
  activeMenu.value = key
}

/**
 * Ferme immédiatement le mega menu desktop (sans délai de survol).
 * @returns {void}
 */
function closeMegaMenuImmediate(): void {
  if (closeTimer) {
    clearTimeout(closeTimer)
    closeTimer = null
  }
  activeMenu.value = null
}

/**
 * Ferme le mega menu après un court délai (sortie de zone au survol).
 * @returns {void}
 */
function closeMenu(): void {
  closeTimer = setTimeout((): void => {
    activeMenu.value = null
    closeTimer = null
  }, 120)
}

/**
 * Ferme le mega menu au clic sur un lien de navigation.
 * @param {MouseEvent} event - Clic dans la zone scrollable du menu.
 * @returns {void}
 */
function onMegaMenuNavClick(event: MouseEvent): void {
  const target: EventTarget | null = event.target
  if (!(target instanceof Element)) return
  if (target.closest('a')) closeMegaMenuImmediate()
}

// ── Mobile ─────────────────────────────────────────────────────────────────
function toggleMobile(): void {
  mobileOpen.value = !mobileOpen.value
}

watch(mobileOpen, (open: boolean): void => {
  if (!import.meta.client) return
  document.body.style.overflow = open ? 'hidden' : ''
})

function toggleAccordion(key: string) {
  openAccordion.value = openAccordion.value === key ? null : key
}

// ── User menu ──────────────────────────────────────────────────────────────
function toggleUserMenu() {
  userMenuOpen.value = !userMenuOpen.value
}
function closeUserMenu() {
  userMenuOpen.value = false
}

async function handleLogout() {
  closeUserMenu()
  await logout()
}

async function handleMobileLogout() {
  await handleLogout()
  mobileOpen.value = false
}

// ── Click outside dropdown / mega menu ─────────────────────────────────────
/**
 * Ferme le menu utilisateur ou le mega menu lors d'un clic en dehors de leurs zones.
 * @param {MouseEvent} event - Événement clic sur le document.
 * @returns {void}
 */
function handleClickOutside(event: MouseEvent): void {
  const target: EventTarget | null = event.target
  if (!(target instanceof Node)) return

  const dropdown: HTMLElement | null = userDropdownEl.value
  const avatarBtn: HTMLButtonElement | null = avatarBtnEl.value
  if (
    userMenuOpen.value &&
    dropdown !== null &&
    !dropdown.contains(target) &&
    avatarBtn !== null &&
    !avatarBtn.contains(target)
  ) {
    userMenuOpen.value = false
  }

  if (!activeMenu.value) return
  if (megaMenuPanelEl.value?.contains(target)) return
  if (navLinksEl.value?.contains(target)) return
  closeMegaMenuImmediate()
}

// ── Scroll ─────────────────────────────────────────────────────────────────
function onScroll() {
  isScrolled.value = window.scrollY > 10
}

onMounted(() => {
  window.addEventListener('scroll', onScroll, { passive: true })
  document.addEventListener('click', handleClickOutside)
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
  document.removeEventListener('click', handleClickOutside)
  mobileOpen.value = false
})
</script>

<style scoped>
/* ── Header ───────────────────────────────────────────────── */
.nav {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: var(--z-header);
  height: var(--header-height);
  background: var(--chat-nav-bg, rgba(7, 7, 15, 0.6));
  backdrop-filter: blur(20px);
  -webkit-backdrop-filter: blur(20px);
  border-bottom: 1px solid var(--border-subtle);
  transition:
    background var(--duration-base) var(--ease-out),
    border-color var(--duration-base) var(--ease-out);
}

.nav--scrolled {
  background: var(--chat-nav-bg-scrolled, rgba(7, 7, 15, 0.92));
  border-bottom-color: var(--border-default);
}

.nav__inner {
  display: flex;
  align-items: center;
  gap: var(--space-6);
  height: 100%;
}

/* ── Logo ─────────────────────────────────────────────────── */
.nav__logo {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  text-decoration: none;
  flex-shrink: 0;
}
.nav__logo-img {
  width: 28px;
  height: 28px;
}
.nav__logo-text {
  font-family: var(--font-display);
  font-weight: 800;
  font-size: var(--text-lg);
  color: var(--text-primary);
  letter-spacing: var(--tracking-tight);
}

/* ── Nav links desktop ────────────────────────────────────── */
.nav__links {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  flex: 1;
}
.nav__item {
  position: relative;
}
.nav__link {
  display: flex;
  align-items: center;
  gap: var(--space-1);
  padding: var(--space-2) var(--space-3);
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-secondary);
  background: none;
  border: none;
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition:
    color var(--duration-fast) var(--ease-out),
    background var(--duration-fast) var(--ease-out);
  white-space: nowrap;
}
.nav__link:hover,
.nav__link--active {
  color: var(--text-primary);
  background: var(--chat-nav-hover-surface, rgba(255, 255, 255, 0.06));
}

/* ── Chevron ──────────────────────────────────────────────── */
.nav__chevron {
  color: var(--text-muted);
  transition: transform var(--duration-fast) var(--ease-out);
  flex-shrink: 0;
}
.nav__chevron--open {
  transform: rotate(180deg);
}

/* ── Actions ──────────────────────────────────────────────── */
.nav__actions {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  flex-shrink: 0;
  position: relative;
}

/* ── Avatar connecté ──────────────────────────────────────── */
.nav__avatar-btn {
  display: flex;
  align-items: center;
  gap: var(--space-2);
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--space-1);
  border-radius: var(--radius-full);
  transition: opacity var(--duration-fast) var(--ease-out);
}
.nav__avatar-btn:hover {
  opacity: 0.85;
}

.nav__avatar-ring {
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: var(--gradient-blue);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border: 2px solid var(--accent-blue);
  flex-shrink: 0;
}
.nav__avatar-ring--lg {
  width: 44px;
  height: 44px;
}
.nav__avatar-ring img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.nav__avatar-initials {
  font-size: var(--text-sm);
  font-weight: 700;
  color: #fff;
  line-height: 1;
}

/* ── Burger ───────────────────────────────────────────────── */
.nav__burger {
  display: none;
  flex-direction: column;
  justify-content: center;
  gap: 5px;
  width: 36px;
  height: 36px;
  background: none;
  border: none;
  cursor: pointer;
  padding: var(--space-2);
  margin-left: auto;
  border-radius: var(--radius-sm);
}
.nav__burger span {
  display: block;
  width: 20px;
  height: 2px;
  background: var(--text-secondary);
  border-radius: 2px;
  transition:
    transform var(--duration-base) var(--ease-out),
    opacity var(--duration-base) var(--ease-out);
}
.nav__burger--open span:nth-child(1) {
  transform: translateY(7px) rotate(45deg);
}
.nav__burger--open span:nth-child(2) {
  opacity: 0;
}
.nav__burger--open span:nth-child(3) {
  transform: translateY(-7px) rotate(-45deg);
}

/* ── Mega menu ────────────────────────────────────────────── */
.mega-menu {
  --mega-menu-max-height: min(60dvh, calc(100dvh - var(--header-height) - var(--space-6)));
  position: fixed;
  top: var(--header-height);
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
}

.mega-menu__backdrop {
  position: absolute;
  inset: 0;
  z-index: 1;
  cursor: default;
}

.mega-menu__panel {
  position: relative;
  z-index: 2;
  width: 100%;
  max-height: var(--mega-menu-max-height);
  overflow-x: hidden;
  overflow-y: auto;
  overscroll-behavior: contain;
  -webkit-overflow-scrolling: touch;
  background: var(--mega-menu-panel-bg, rgba(10, 10, 22, 0.97));
  backdrop-filter: blur(24px);
  -webkit-backdrop-filter: blur(24px);
  border-bottom: 1px solid var(--border-default);
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.6);
}

.mega-menu__inner {
  display: flex;
  gap: var(--space-12);
  padding-block: var(--space-8);
}
.mega-col {
  min-width: 200px;
}
.mega-col--wide {
  min-width: 360px;
}
.mega-col--cta {
  margin-left: auto;
  min-width: 240px;
}
.mega-col__title {
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: var(--tracking-wider);
  text-transform: uppercase;
  color: var(--text-muted);
  margin-bottom: var(--space-4);
}
.mega-col__subtitle {
  font-size: var(--text-xs);
  font-weight: 700;
  letter-spacing: var(--tracking-wider);
  text-transform: uppercase;
  color: var(--text-muted);
  margin-top: var(--space-4);
  margin-bottom: var(--space-3);
}
.mega-col__list {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}
.mega-col__list--grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: var(--space-1) var(--space-4);
}
.mega-col__list--inline {
  flex-direction: row;
  flex-wrap: wrap;
  gap: var(--space-2);
}
.mega-col__list--diplomes {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: var(--space-2) var(--space-4);
}
.mega-link {
  display: block;
  font-size: var(--text-sm);
  color: var(--text-secondary);
  text-decoration: none;
  padding: var(--space-1) var(--space-2);
  border-radius: var(--radius-sm);
  transition:
    color var(--duration-fast) var(--ease-out),
    background var(--duration-fast) var(--ease-out);
}
.mega-link:hover {
  color: var(--text-primary);
  background: var(--chat-nav-hover-surface, rgba(255, 255, 255, 0.05));
}
.mega-link--all {
  margin-top: var(--space-3);
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--accent-blue-lt);
  padding-left: 0;
  letter-spacing: var(--tracking-wider);
  text-transform: uppercase;
  margin-bottom: var(--space-4);
}
.mega-link--all-next {
  margin-top: 0;
}
.mega-link--diploma {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}
.mega-link--article {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-4);
}
.mega-link__read-time {
  font-size: var(--text-xs);
  color: var(--text-muted);
  white-space: nowrap;
}
.mega-cta {
  background: rgba(3, 160, 241, 0.06);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  padding: var(--space-5);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.mega-cta__text {
  font-size: var(--text-sm);
  color: var(--text-secondary);
  line-height: var(--leading-relaxed);
}
.mega-cta__question {
  font-size: var(--text-xs);
  color: var(--text-muted);
  font-style: italic;
}

/* ── User dropdown ────────────────────────────────────────── */
.user-dropdown {
  position: absolute;
  top: calc(100% + var(--space-2));
  right: 0;
  width: 280px;
  background: var(--bg-surface);
  border: 1px solid var(--border-default);
  border-radius: var(--radius-lg);
  box-shadow: var(--shadow-lg);
  z-index: 300;
  overflow: hidden;
}
.user-dropdown__header {
  padding: var(--space-4);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.user-dropdown__identity {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}
.user-dropdown__name {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: var(--text-base);
  color: var(--text-primary);
  line-height: 1.2;
}
.user-dropdown__mbti {
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--accent-blue-lt);
}
.user-dropdown__progress {
  display: flex;
  align-items: center;
  gap: var(--space-2);
}
.user-dropdown__pct {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
}
.user-dropdown__hint {
  font-size: var(--text-xs);
  color: var(--text-secondary);
  line-height: var(--leading-snug);
}
.user-dropdown__item {
  display: block;
  width: 100%;
  padding: var(--space-3) var(--space-4);
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-primary);
  background: none;
  border: none;
  cursor: pointer;
  text-align: left;
  text-decoration: none;
  transition: background var(--duration-fast) var(--ease-out);
}
.user-dropdown__item:hover {
  background: var(--chat-nav-hover-surface, rgba(255, 255, 255, 0.05));
}
.user-dropdown__item--danger {
  color: var(--color-error);
}
.user-dropdown__item--danger:hover {
  background: rgba(239, 68, 68, 0.08);
}

/* ── Mobile menu ──────────────────────────────────────────── */
.mobile-menu {
  position: fixed;
  top: var(--header-height);
  left: 0;
  right: 0;
  bottom: 0;
  background: var(--bg-surface);
  border-top: 1px solid var(--border-subtle);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  z-index: var(--z-header);
}
.mobile-menu__body {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
  padding: var(--space-4) var(--container-pad);
  padding-bottom: var(--space-2);
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
}
.mobile-menu__footer {
  flex-shrink: 0;
  padding: var(--space-4) var(--container-pad);
  padding-bottom: calc(var(--space-6) + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid var(--border-subtle);
  background: var(--bg-surface);
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.mobile-menu__footer-btn {
  width: 100%;
  justify-content: center;
  text-align: center;
}
.mobile-menu__stats {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: var(--space-2);
  font-size: var(--text-xs);
  color: var(--text-muted);
}
.mobile-menu__dot {
  opacity: 0.4;
}

/* ── Bloc profil mobile ───────────────────────────────────── */
.mobile-profile-block {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
  padding: var(--space-5) 0 var(--space-4);
  border-bottom: 1px solid var(--border-subtle);
  margin-bottom: var(--space-2);
}
.mobile-profile-block__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: var(--space-3);
}
.mobile-profile-block__identity {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  flex: 1;
  min-width: 0;
}
.mobile-profile-block__info {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.mobile-profile-block__name {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: var(--text-base);
  color: var(--text-primary);
  line-height: 1.2;
}
.mobile-profile-block__mbti {
  font-size: var(--text-sm);
  font-weight: 700;
  color: var(--accent-blue-lt);
}
.mobile-profile-block__progress {
  display: flex;
  align-items: center;
  gap: var(--space-3);
}
.mobile-profile-block__pct {
  font-size: var(--text-sm);
  font-weight: 600;
  color: var(--text-primary);
  white-space: nowrap;
}
.mobile-profile-block__hint {
  font-size: var(--text-xs);
  color: var(--text-secondary);
  line-height: var(--leading-snug);
}

/* ── Accordéon mobile ─────────────────────────────────────── */
.mobile-accordion {
  border-bottom: 1px solid var(--border-subtle);
}
.mobile-accordion__trigger {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  padding: var(--space-4) var(--space-2);
  font-size: var(--text-base);
  font-weight: 600;
  color: var(--text-primary);
  background: none;
  border: none;
  cursor: pointer;
  border-radius: var(--radius-sm);
  transition: background var(--duration-fast) var(--ease-out);
}
.mobile-accordion__trigger:hover {
  background: var(--chat-nav-hover-surface, rgba(255, 255, 255, 0.04));
}
.mobile-accordion__list {
  display: flex;
  flex-direction: column;
  gap: var(--space-1);
  overflow: hidden;
  padding-bottom: var(--space-2);
}
.mobile-accordion__link {
  display: block;
  font-size: var(--text-sm);
  color: var(--text-secondary);
  padding: var(--space-2) var(--space-4);
  border-radius: var(--radius-sm);
  text-decoration: none;
  transition:
    color var(--duration-fast) var(--ease-out),
    background var(--duration-fast) var(--ease-out);
}
.mobile-accordion__link:hover {
  color: var(--text-primary);
  background: var(--chat-nav-hover-surface, rgba(255, 255, 255, 0.05));
}

/* ── Liens compte mobile ──────────────────────────────────── */
.mobile-account-link {
  display: flex;
  align-items: center;
  gap: var(--space-3);
  width: 100%;
  padding: var(--space-3) var(--space-1);
  font-size: var(--text-sm);
  font-weight: 500;
  color: var(--text-secondary);
  background: none;
  border: none;
  cursor: pointer;
  text-decoration: none;
  border-radius: var(--radius-sm);
  transition:
    color var(--duration-fast) var(--ease-out),
    background var(--duration-fast) var(--ease-out);
}
.mobile-account-link:hover {
  color: var(--text-primary);
  background: var(--chat-nav-hover-surface, rgba(255, 255, 255, 0.04));
}
.mobile-account-link--danger {
  color: var(--color-error);
}
.mobile-account-link--danger:hover {
  background: rgba(239, 68, 68, 0.08);
  color: var(--color-error);
}

/* ── Transitions ──────────────────────────────────────────── */
.mega-enter-active,
.mega-leave-active {
  transition:
    opacity var(--duration-fast) var(--ease-out),
    transform var(--duration-fast) var(--ease-out);
}
.mega-enter-from,
.mega-leave-to {
  opacity: 0;
  transform: translateY(-8px);
}

.accordion-enter-active,
.accordion-leave-active {
  transition:
    opacity var(--duration-base) var(--ease-out),
    max-height var(--duration-base) var(--ease-out);
  max-height: 400px;
}
.accordion-enter-from,
.accordion-leave-to {
  opacity: 0;
  max-height: 0;
}

.mobile-menu-enter-active,
.mobile-menu-leave-active {
  transition:
    opacity var(--duration-base) var(--ease-out),
    transform var(--duration-base) var(--ease-out);
}
.mobile-menu-enter-from,
.mobile-menu-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

.fade-enter-active,
.fade-leave-active {
  transition:
    opacity var(--duration-fast) var(--ease-out),
    transform var(--duration-fast) var(--ease-out);
}
.fade-enter-from,
.fade-leave-to {
  opacity: 0;
  transform: translateY(-6px);
}

/* ── Responsive ───────────────────────────────────────────── */
@media (max-width: 1150px) {
  .nav__links {
    display: none;
  }
  .nav__actions {
    display: none;
  }
  .nav__burger {
    display: flex;
  }
}
@media (min-width: 1151px) {
  .mobile-menu {
    display: none !important;
  }
  .nav__burger {
    display: none !important;
  }
}
</style>
